import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import ManagerLayout from "@/components/layout/ManagerLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PlusCircle, Building, CalendarCheck, DollarSign, Clock, Boxes, BarChart3, MapPin, Loader2 } from "lucide-react";
import { PropertyStatus, type Property } from "@shared/schema";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from '@/lib/api';

export default function ManagerDashboard() {
  const { user } = useAuth();

  // Fetch properties owned by the manager
  const { data: properties = [], isLoading: propertiesLoading } = useQuery<Property[]>({
    queryKey: ["my-properties"],
    enabled: !!user,
    queryFn: async () => {
      try {
        console.log("Fetching properties for dashboard");
        const data = await apiRequest('GET', 'my-properties');
        console.log("Properties data received:", data);
        return data || [];
      } catch (error) {
        console.error("Error fetching properties:", error);
        return [];
      }
    },
  });
  
  // Fetch bookings
  const { data: bookings = [], isLoading: bookingsLoading } = useQuery({
    queryKey: ['property-bookings/my'],
    enabled: !!user,
    queryFn: async () => {
      try {
        const data = await apiRequest('GET', 'property-bookings/my');
        console.log("Bookings data received:", data);
        return data || [];
      } catch (error) {
        console.error("Error fetching bookings:", error);
        return [];
      }
    },
  });
  
  // Fetch earnings data
  const { data: earnings, isLoading: earningsLoading } = useQuery({
    queryKey: ['manager/earnings'],
    enabled: !!user,
    queryFn: async () => {
      try {
        const res = await apiRequest('GET', 'manager/earnings');
        // if (!res.ok) {
        //   console.error("Failed to fetch earnings:", res.statusText);
        //   return { totalEarnings: 0, pendingPayouts: 0, paidEarnings: 0 };
        // }
        // const data = await res.json();
        const data =  res;
        console.log("Earnings data received:", data);
        return data;
      } catch (error) {
        console.error("Failed to fetch earnings:", error);
        return { totalEarnings: 0, pendingPayouts: 0, paidEarnings: 0 };
      }
    }
  });
  
  // Calculate real stats based on the real data
  const stats = {
    propertyCount: properties.length,
    pendingBookings: bookings.filter(b => b.status === 'pending').length,
    activeBookings: bookings.filter(b => b.status === 'confirmed' || b.status === 'pending').length,
    monthlyRevenue: earnings?.totalEarnings || user?.totalEarnings || 0,
    occupancyRate: properties.length > 0 ? Math.min(Math.round((bookings.filter(b => b.status === 'confirmed').length / properties.length) * 100), 100) : 0,
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Get status badge
  const getStatusBadge = (status: PropertyStatus) => {
    switch (status) {
      case PropertyStatus.ACTIVE:
        return <Badge className="bg-green-500 hover:bg-green-600">Active</Badge>;
      case PropertyStatus.PENDING:
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">Pending</Badge>;
      case PropertyStatus.INACTIVE:
        return <Badge className="bg-gray-500 hover:bg-gray-600">Inactive</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <ManagerLayout>
      <div className="container py-6 max-w-7xl">
        <div className="flex flex-col gap-6">
          {/* Page title and add property button */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Manager Dashboard</h1>
              <p className="text-muted-foreground">Welcome back to your property management dashboard</p>
            </div>
            {user?.status === 'pending' || user?.status === 'inactive' ? (
              <Button disabled title="Your account must be approved to add properties" className="cursor-not-allowed opacity-70">
                <PlusCircle className="mr-2 h-4 w-4" /> Add New Property
              </Button>
            ) : (
              <Link href="/manager/properties">
                <Button>
                  <PlusCircle className="mr-2 h-4 w-4" /> Add New Property
                </Button>
              </Link>
            )}
          </div>

          {/* Stats cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Properties</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold">{stats.propertyCount}</div>
                  <Building className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.propertyCount > 0 
                    ? "Manage your properties" 
                    : "Add your first property"}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Active Bookings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold">{stats.activeBookings}</div>
                  <CalendarCheck className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.pendingBookings} pending confirmation
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Monthly Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold">{formatCurrency(stats.monthlyRevenue)}</div>
                  <DollarSign className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  For the current month
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Occupancy Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold">{stats.occupancyRate}%</div>
                  <div className="w-14 h-6 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500" 
                      style={{width: `${stats.occupancyRate}%`}}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Average across all properties
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Recent properties and bookings - 2 columns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent properties */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <CardTitle>Recent Properties</CardTitle>
                  <Link href="/manager/properties">
                    <Button variant="ghost" size="sm">View All</Button>
                  </Link>
                </div>
                <CardDescription>Your recently added properties</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {properties.slice(0, 3).map((property) => {
                    const propertyImages = property.images || [];
                    const primaryImage = propertyImages.find(img => img.isPrimary) || propertyImages[0];
                    
                    return (
                      <div key={property.id} className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded overflow-hidden bg-gray-100">
                          {primaryImage ? (
                            <img 
                              src={primaryImage.imageUrl} 
                              alt={property.title} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <MapPin className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{property.title}</p>
                          <p className="text-sm text-muted-foreground">{property.area}</p>
                        </div>
                        <div className="flex flex-col items-end">
                          {getStatusBadge(property.status)}
                          <p className="text-xs text-muted-foreground mt-1">
                            Added {formatDate(property.createdAt)}
                          </p>
                        </div>
                      </div>
                    );
                  })}

                  {properties.length === 0 && (
                    <div className="text-center py-6">
                      <Boxes className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">No properties yet</p>
                      <Link href="/manager/properties">
                        <Button className="mt-2" size="sm">
                          <PlusCircle className="mr-2 h-4 w-4" /> Add Property
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent bookings */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <CardTitle>Recent Bookings</CardTitle>
                  <Link href="/manager/bookings">
                    <Button variant="ghost" size="sm">View All</Button>
                  </Link>
                </div>
                <CardDescription>Your recent guest bookings</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {bookingsLoading ? (
                    <div className="flex justify-center items-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mr-2" />
                      <span className="text-sm text-muted-foreground">Loading bookings...</span>
                    </div>
                  ) : bookings.length > 0 ? (
                    bookings.slice(0, 3).map((booking) => {
                      const formatBookingDates = () => {
                        if (booking.checkIn && booking.checkOut) {
                          return `${formatDate(booking.checkIn)} - ${formatDate(booking.checkOut)}`;
                        }
                        return "Dates not specified";
                      };
                      
                      return (
                        <div key={booking.id} className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Clock className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{booking.guestName || "Guest"}</p>
                            <p className="text-sm text-muted-foreground">
                              {booking.propertyTitle || `Property #${booking.propertyId}`} • {formatBookingDates()}
                            </p>
                          </div>
                          <div className="flex flex-col items-end">
                            <Badge className={
                              booking.status === "confirmed" ? "bg-green-500 hover:bg-green-600" : 
                              booking.status === "pending" ? "bg-yellow-500 hover:bg-yellow-600" : 
                              "bg-gray-500 hover:bg-gray-600"
                            }>
                              {booking.status?.charAt(0).toUpperCase() + booking.status?.slice(1) || "Unknown"}
                            </Badge>
                            <p className="text-sm font-medium mt-1">
                              {formatCurrency(booking.totalAmount ? booking.totalAmount / 100 : 0)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-6">
                      <CalendarCheck className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">No bookings yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Performance chart placeholder */}
          <Card>
            <CardHeader>
              <CardTitle>Revenue Performance</CardTitle>
              <CardDescription>Your revenue trends over the last 30 days</CardDescription>
            </CardHeader>
            <CardContent className="h-80 flex items-center justify-center">
              <div className="text-center">
                <BarChart3 className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
                <p className="text-muted-foreground mb-2">Performance charts coming soon</p>
                <p className="text-xs text-muted-foreground max-w-md mx-auto">
                  Track your property performance, booking trends, and revenue analytics
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </ManagerLayout>
  );
}